"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type AddQuoteResult,
  type GlobalContentPage,
  type HouseholderCommonPage,
  type HouseholderEntryPage,
  type HouseholderQuote,
  HouseholderType,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ComposedDropdownSelect } from "@/components/shared/composedDropdownSelect/ComposedDropdownSelect";
import { ComposedInputField } from "@/components/shared/composedInputField/ComposedInputField";
import {
  ComposedRadioButton,
  ComposedRadioButtonValue,
} from "@/components/shared/composedRadioButton/ComposedRadioButton";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import { HouseholderEntryValidationSchema } from "@/validators/products/householder/HouseholderEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: HouseholderEntryPage | null;
  externalCommonContent?: HouseholderCommonPage | null;
  externalGlobalContent?: GlobalContentPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: HouseholderQuote) => Promise<AddQuoteResult>;
};

export const HouseholderEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
  externalCommonContent,
  externalGlobalContent,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content: householderEntryPage,
    siteContent,
    isLoading,
    submitHandler,
  } = useEntry<HouseholderQuote, HouseholderEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "householderEntryPage",
    fetchSiteContent:
      !externalContent || !externalCommonContent || !externalGlobalContent,
  });
  const householderCommonPage =
    externalCommonContent ?? siteContent?.householderCommonPage;
  const globalContent = externalGlobalContent ?? siteContent?.globalContent;

  const yesAndNoAnswer = globalContent?.yesAndNoAnswer;
  const defaultValues: HouseholderQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    isHousingCompany: true,
    productType: ProductType.HOUSEHOLDER,
    householder: {
      livingSpace: undefined,
      householderType: HouseholderType.APARTMENT,
    },
  };
  const { handleSubmit, control, formState, watch } = useForm<HouseholderQuote>(
    {
      defaultValues,
      resolver: yupResolver(
        HouseholderEntryValidationSchema(householderEntryPage),
      ),
      disabled: isLoading,
    },
  );

  const householderType = watch("householder.householderType");
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        {householderEntryPage?.entryPageCommon?.title?.value && (
          <h1 className="if typography heading small">
            {householderEntryPage?.entryPageCommon?.title?.value}
          </h1>
        )}
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="householder.householderType"
            render={({ field }) => (
              <ComposedDropdownSelect
                id="householder.householderType"
                errorMessage={errors?.householder?.householderType?.message}
                label={
                  householderEntryPage?.householderTypeQuestion?.label?.value ??
                  ""
                }
                placeholder={
                  householderEntryPage?.householderTypeQuestion?.placeholder
                    ?.value ?? undefined
                }
                {...field}
                value={field.value ?? ""}
              >
                <option value={HouseholderType.APARTMENT}>
                  {householderCommonPage?.householderTypeApartmentLabel?.value}
                </option>
                <option value={HouseholderType.ROW_HOUSE}>
                  {householderCommonPage?.householderTypeRowHouseLabel?.value}
                </option>
                <option value={HouseholderType.PAIR_HOUSE}>
                  {householderCommonPage?.householderTypePairHouseLabel?.value}
                </option>
              </ComposedDropdownSelect>
            )}
          />
          <Controller
            control={control}
            name="householder.livingSpace"
            render={({ field }) => (
              <ComposedInputField
                id="householder.livingSpace"
                errorMessage={errors?.householder?.livingSpace?.message}
                popoverMessage={
                  householderEntryPage?.livingSpaceQuestion?.popover?.value
                }
                placeholder={
                  householderEntryPage?.livingSpaceQuestion?.placeholder
                    ?.value ?? undefined
                }
                {...field}
                value={field.value ?? ""}
                type={"number"}
              >
                {householderEntryPage?.livingSpaceQuestion?.label?.value}
              </ComposedInputField>
            )}
          />
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                id="policyHolderSsn"
                placeholder={
                  householderEntryPage?.entryPageCommon?.ssnQuestion
                    ?.placeholder?.value ?? undefined
                }
                useDefaultPopover={false}
                popoverMessage={
                  householderEntryPage?.entryPageCommon?.ssnQuestion?.popover
                    ?.value ?? ""
                }
                errorMessage={errors?.policyHolderSsn?.message}
                {...field}
              >
                {
                  householderEntryPage?.entryPageCommon?.ssnQuestion?.label
                    ?.value
                }
              </SsnInput>
            )}
          />
          {householderType === HouseholderType.PAIR_HOUSE && (
            <Controller
              control={control}
              name="isHousingCompany"
              render={({ field }) => (
                <ComposedRadioButton
                  disabled={isLoading}
                  id="isHousingCompany"
                  label={
                    householderEntryPage?.isHousingCompanyQuestion?.label
                      ?.value ?? "isHousingCompany"
                  }
                  errorMessage={errors?.isHousingCompany?.message}
                  popoverMessage={
                    householderEntryPage?.isHousingCompanyQuestion?.popover
                      ?.value
                  }
                  invalid={undefined}
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(e) => field.onChange(e.target.value === "true")}
                >
                  <ComposedRadioButtonValue
                    disabled={isLoading}
                    defaultChecked={true}
                    name="isHousingCompany"
                    id="isHousingCompany.Yes"
                    label={yesAndNoAnswer?.yes?.value}
                    value={"true"}
                  />
                  <ComposedRadioButtonValue
                    disabled={isLoading}
                    name="isHousingCompany"
                    id="isHousingCompany.No"
                    label={yesAndNoAnswer?.no?.value}
                    value={"false"}
                  />
                </ComposedRadioButton>
              )}
            />
          )}
          <ProductContainer.CallToAction
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
          >
            {householderEntryPage?.entryPageCommon?.submitButtonText?.value ??
              "Katso hinta"}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
